export interface BreakoutTheme {
  name: string;
  color: string;
}

export const BREAKOUT_ROOM_NAMES: string[] = [
  "Team Vitalis",
  "Team Zukunft",
  "MedInnovators",
  "Die Pioniere",
  "Team Präzision",
  "MedAlliance",
  "Die Vordenker",
  "Team Fortschritt",
  "Team Heilkraft",
  "Die Visionäre",
  "MedSynergie",
  "Team Impuls",
  "Die Wegbereiter",
  "Team Horizont",
  "MedKompass",
  "Team Dynamik",
  "Die Strategen",
  "Team Balance",
  "MedFokus",
  "Team Exzellenz",
  "Die Gestalter",
  "Team Dialog",
  "MedDialog",
  "Team Harmonie",
  "Die Brückenbauer",
  "Team Klarheit",
  "MedPraxis",
  "Team Vertrauen",
  "Die Impulsgeber",
  "Team Weitblick",
  "MedNetzwerk",
  "Team Energie",
  "Die Entdecker",
  "Team Konsens",
  "MedZentrum",
  "Team Perspektive",
  "Die Mentoren",
  "Team Aufbruch",
  "MedKonzept",
  "Team Resonanz",
  "Die Navigatoren",
  "Team Einklang",
  "MedPuls",
  "Team Spektrum",
  "Die Architekten",
  "Team Vorsprung",
  "MedEinheit",
  "Team Synthese",
  "Die Katalysatoren",
  "Team Momentum"
];

export const BREAKOUT_COLOR_SHADES: string[] = [
  "#06B6D4",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#2DD4BF",
  "#38BDF8",
  "#60A5FA",
  "#818CF8",
  "#A78BFA",
  "#C084FC",
  "#E879F9",
  "#F472B6",
  "#FB7185",
  "#F87171",
  "#FB923C",
  "#FBBF24",
  "#FACC15",
  "#A3E635",
  "#4ADE80",
  "#34D399",
  "#22D3EE",
  "#0284C7",
  "#2563EB",
  "#4F46E5",
  "#7C3AED",
  "#9333EA",
  "#C026D3",
  "#DB2777",
  "#E11D48",
  "#EA580C",
  "#D97706",
  "#65A30D",
  "#16A34A",
  "#059669",
  "#0D9488",
  "#0891B2",
  "#4338CA"
];

function shuffleCopy<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Assign unique-ish theme pairs when creating breakout rooms (server-side at creation). */
export function pickUniqueBreakoutThemes(count: number): BreakoutTheme[] {
  const names = shuffleCopy(BREAKOUT_ROOM_NAMES);
  const colors = shuffleCopy(BREAKOUT_COLOR_SHADES);
  return Array.from({ length: count }, (_, index) => ({
    name: names[index % names.length],
    color: colors[index % colors.length]
  }));
}

export function pickAdditionalBreakoutTheme(usedNames: Set<string>, usedColors: Set<string>): BreakoutTheme {
  const name = shuffleCopy(BREAKOUT_ROOM_NAMES).find((candidate) => !usedNames.has(candidate)) ?? "Team Momentum";
  const color =
    shuffleCopy(BREAKOUT_COLOR_SHADES).find((candidate) => !usedColors.has(candidate)) ?? BREAKOUT_COLOR_SHADES[0];
  return { name, color };
}

/** Legacy fallback when themeColor is missing on older rows. */
export function getBreakoutTheme(seed: string | number): BreakoutTheme {
  const num = typeof seed === "number" ? seed : Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return {
    name: BREAKOUT_ROOM_NAMES[num % BREAKOUT_ROOM_NAMES.length],
    color: BREAKOUT_COLOR_SHADES[(num * 7) % BREAKOUT_COLOR_SHADES.length]
  };
}

export function resolveBreakoutTheme(room: { id: string; name: string; themeColor?: string | null }): BreakoutTheme {
  const color =
    typeof room.themeColor === "string" && room.themeColor ? room.themeColor : getBreakoutTheme(room.id).color;
  return { name: room.name, color };
}
